import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { Logo } from '../../components/brand/Logo'
import { Icon } from '../../components/ui/icons'
import { formatTime } from '../../utils/format'

/**
 * Pantalla de llamados para el monitor de la sala de espera (H1).
 *
 * Se abre sin sesión — es un kiosco, no una estación de trabajo — y por eso
 * muestra solo lo mínimo: nombre abreviado ("Juan P.") y box. El backend no
 * entrega RUT, prestación ni profesional (minimización de datos, Ley 21.719).
 *
 * Tipografía muy grande a propósito: se lee desde el fondo de la sala.
 */

interface WaitingRoomCall {
  id: string
  name: string
  box: string | null
  calledAt: string
  callCount: number
}

interface WaitingRoomData {
  location: { id: string; name: string }
  calls: WaitingRoomCall[]
}

/** Cada cuánto se refresca la pantalla. Suficientemente ágil, sin martillar la API. */
const REFRESH_MS = 5000

export function WaitingRoomScreen() {
  const [searchParams] = useSearchParams()
  const locationId = searchParams.get('sede') ?? ''

  const locations = useQuery({
    queryKey: ['waiting-room-locations'],
    queryFn: async () => (await axios.get<Array<{ id: string; name: string }>>('/api/public/waiting-room')).data,
    enabled: !locationId,
  })

  const screen = useQuery({
    queryKey: ['waiting-room', locationId],
    queryFn: async () => (await axios.get<WaitingRoomData>(`/api/public/waiting-room/${locationId}`)).data,
    enabled: Boolean(locationId),
    refetchInterval: REFRESH_MS,
  })

  // Sin sede en la URL: selector para dejar el monitor apuntando a la correcta.
  if (!locationId) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-surface p-8 text-center">
        <Logo className="h-14" />
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Pantalla de sala de espera</h1>
          <p className="mt-1 text-steel-600">Elige la sede que mostrará este monitor.</p>
        </div>
        <ul className="flex flex-wrap justify-center gap-3">
          {(locations.data ?? []).map((l) => (
            <li key={l.id}>
              <a
                href={`/llamados?sede=${l.id}`}
                className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-6 py-3 font-display font-semibold text-white hover:bg-brand-700"
              >
                <Icon name="location" className="h-4 w-4" />
                {l.name}
              </a>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  const calls = screen.data?.calls ?? []
  const [current, ...previous] = calls

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <header className="flex items-center justify-between border-b border-steel-100 bg-white px-8 py-4">
        <Logo className="h-12" />
        <p className="font-display text-xl font-semibold text-steel-600">
          {screen.data?.location.name ?? 'Sala de espera'}
        </p>
      </header>

      <main className="flex flex-1 flex-col justify-center gap-8 px-8 py-10">
        {calls.length === 0 ? (
          <div className="text-center">
            <Icon name="clock" className="mx-auto h-16 w-16 text-brand-300" />
            <p className="mt-4 font-display text-3xl font-semibold text-steel-500">
              Sin llamados por ahora
            </p>
            <p className="mt-2 text-lg text-steel-500">Espera a ser llamado por tu nombre.</p>
          </div>
        ) : (
          <>
            {/* Llamado actual, en grande */}
            <div className="rounded-[2.5rem] border border-brand-200 bg-white p-10 text-center shadow-[0_20px_60px_-40px_rgba(18,121,166,0.6)]">
              <p className="font-display text-sm font-semibold uppercase tracking-[0.3em] text-brand-600">
                Llamando ahora
              </p>
              <p className="mt-4 font-display text-6xl font-bold leading-none text-ink md:text-8xl">{current.name}</p>
              {current.box && (
                <p className="mt-6 inline-flex items-center gap-3 rounded-full bg-brand-600 px-8 py-3 font-display text-3xl font-bold text-white md:text-4xl">
                  <Icon name="location" className="h-7 w-7" />
                  {current.box}
                </p>
              )}
              <p className="mt-5 text-lg text-steel-500">
                {formatTime(current.calledAt)}
                {current.callCount > 1 ? ` · llamado ${current.callCount}` : ''}
              </p>
            </div>

            {/* Llamados anteriores, más chicos */}
            {previous.length > 0 && (
              <div>
                <p className="mb-3 text-center font-display text-sm font-semibold uppercase tracking-[0.25em] text-steel-500">
                  Llamados anteriores
                </p>
                <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {previous.map((c) => (
                    <li
                      key={c.id}
                      className="flex items-center justify-between gap-3 rounded-2xl border border-steel-100 bg-white px-5 py-4"
                    >
                      <span className="font-display text-2xl font-semibold text-ink">{c.name}</span>
                      <span className="text-right">
                        {c.box && <span className="block font-display text-xl font-bold text-brand-700">{c.box}</span>}
                        <span className="text-sm text-steel-500">{formatTime(c.calledAt)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </main>

      <footer className="border-t border-steel-100 bg-white px-8 py-3 text-center text-sm text-steel-500">
        Si no escuchas tu nombre, acércate a recepción.
      </footer>
    </div>
  )
}
